import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { BACKEND_URL } from "../lib/backend";
import { useAuth } from "../AuthContext";
import usePulseLocale from "./i18n";
import { tokens } from "./theme";
import { savePulseNext } from "./PulseGate";

const API = `${BACKEND_URL}/api`;

/** In-page magic-link signup for the Italy landing: same endpoint and consent as /auth,
 *  `consent_source` tags the MC `user_registered` event, `next` = /pulse/italy. */
export default function InlineRegister({ dm, source = "pulse_italy_landing", next = "/pulse/italy", testid = "landing-register" }) {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [consented, setConsented] = useState(false);
  const [state, setState] = useState("idle");   // idle | loading | sent | error
  const [error, setError] = useState("");
  const target = lang === "it" ? `${next}?lang=it` : next;

  const submit = async (e) => {
    e.preventDefault();
    const addr = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(addr)) { setError(t("lr_err_email")); setState("error"); return; }
    if (!consented) { setError(t("lr_err_consent")); setState("error"); return; }
    setError(""); setState("loading");
    // keep a deeper Pulse `next` chosen on the page (e.g. the accuracy table), else land on the deep dive
    let resume = null;
    try { resume = localStorage.getItem("pulse_next"); } catch { /* noop */ }
    if (!resume || !resume.startsWith("/pulse/")) savePulseNext(target);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        email: addr, privacy_consented: true, consent_source: source,
      });
      if (res.data?.dev_token) {       // preview environments return the token inline
        const v = await axios.post(`${API}/auth/verify`, { token: res.data.dev_token });
        login(v.data.access_token, v.data.user, v.data.csrf_token);
        navigate((resume && resume.startsWith("/pulse/")) ? resume : target);
        return;
      }
      setState("sent");
    } catch (err) {
      const d = err.response?.data?.detail;
      const raw = typeof d === "string" ? d : d?.detail;
      const msg = err.response?.status === 403 && (d === "newsletter_inactive" || d?.code === "newsletter_inactive")
        ? t("lr_err_inactive") : (/invalid email/i.test(raw || "") ? t("lr_err_email") : raw || t("lr_err_generic"));
      setError(msg); setState("error");
    }
  };

  if (state === "sent") {
    return (
      <div className="p-5 flex items-start gap-3" style={{ background: T.brandSoft, border: `1px solid ${T.border}`, borderRadius: 2 }} data-testid={`${testid}-sent`}>
        <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: T.accent }} aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold">{t("lr_sent_title")}</p>
          <p className="text-xs mt-1" style={{ color: T.muted }}>{t("lr_sent_body", { email })}</p>
        </div>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-3" data-testid={`${testid}-form`} noValidate>
      <div className="flex flex-col sm:flex-row gap-2">
        <label className="sr-only" htmlFor={`${testid}-email`}>{t("lr_email")}</label>
        <div className="flex-1 flex items-center gap-2 px-3" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 2 }}>
          <Mail className="w-4 h-4 flex-shrink-0" style={{ color: T.muted }} aria-hidden="true" />
          <input id={`${testid}-email`} type="email" required autoComplete="email" value={email} placeholder={t("lr_email_ph")}
            onChange={(e) => { setEmail(e.target.value); if (error) { setError(""); setState("idle"); } }}
            className="w-full py-3 text-sm outline-none bg-transparent" style={{ color: T.text }}
            data-testid={`${testid}-email`} />
        </div>
        <button type="submit" disabled={state === "loading"} data-testid={`${testid}-submit`}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold disabled:opacity-60"
          style={{ background: T.brand, color: "#fff", borderRadius: 2 }}>
          {state === "loading" ? t("lr_sending") : t("lr_submit")} <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
      <label className="flex items-start gap-2 cursor-pointer text-xs leading-relaxed" style={{ color: T.muted }} data-testid={`${testid}-consent-label`}>
        <input type="checkbox" checked={consented} onChange={(e) => { setConsented(e.target.checked); if (error) { setError(""); setState("idle"); } }}
          className="mt-0.5 w-4 h-4 flex-shrink-0 accent-[#006874]" data-testid={`${testid}-consent`} />
        <span>
          {t("lr_consent_1")} <Link to="/privacy" target="_blank" className="underline underline-offset-2" style={{ color: T.accent }}>{t("lr_privacy")}</Link> {t("lr_consent_2")}
        </span>
      </label>
      {error && <p className="text-sm px-3 py-2" style={{ color: "#B42318", background: "rgba(180,35,24,0.08)", borderRadius: 2 }} role="alert" data-testid={`${testid}-error`}>{error}</p>}
      <p className="text-xs" style={{ color: T.muted }}>
        {t("lr_have_account")}{" "}
        <Link to="/auth" onClick={() => savePulseNext(target)} className="font-semibold underline underline-offset-4" style={{ color: T.accent }} data-testid={`${testid}-login`}>{t("lr_login")}</Link>
      </p>
    </form>
  );
}
